import { useEffect, useState } from "react";
import { request as apiRequest } from "@sharpana/auth/api_client/allauth";
import { notifyPurchaseConfirmed } from "@sharpana/shared/utils/webViewBridge";

export type PostCheckoutStatus = "idle" | "loading" | "success" | "error";

export interface UsePostCheckoutResult {
  status: PostCheckoutStatus;
  hasActiveSubscription: boolean;
  tokensGranted: number;
}

/**
 * Settle a RevenueCat purchase and tell the native wrapper it can drop its
 * "finalizing…" overlay.
 *
 * The wrapper navigates here after a successful native purchase and then
 * blocks on `PURCHASE_CONFIRMED`; if this never fires the user stares at a
 * spinner. So the notify happens as soon as the grant is confirmed, before
 * any of the page's own rendering decisions.
 *
 * @param route the path to report back, purely for the wrapper's logs
 */
export function usePostCheckout(route: string): UsePostCheckoutResult {
  const [status, setStatus] = useState<PostCheckoutStatus>("idle");
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [tokensGranted, setTokensGranted] = useState(0);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setStatus("loading");
        const resp: any = await apiRequest(
          "POST",
          "/api/confirm-payment-grant-tokens-revcat",
          {}
        );
        if (cancelled) return;

        if (resp && resp.success) {
          setHasActiveSubscription(!!resp.has_active_subscription);
          setTokensGranted(Number(resp.tokens_granted ?? 0));
          setStatus("success");
          notifyPurchaseConfirmed(route);
        } else {
          setStatus("error");
        }
      } catch {
        if (!cancelled) setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { status, hasActiveSubscription, tokensGranted };
}
